import type {
  CostSummaryBand,
  CostSummaryMileage,
  CostSummaryPercentiles,
  CostSummaryTrend,
  MarketplaceCostSummary,
} from "@/util/cost-summary"
import { hasFinitePercentiles, hasUsableMileage } from "@/util/cost-summary"
import {
  describeMarketplaceComparison,
  hasRenderableTrend,
} from "@/util/describe-trend"

// Pure view model for the cost section, shared by the pet city and state pages.
// It owns the three decisions that are easy to get wrong:
//   1. Band source — the page's own (city/state) band when it clears the feed
//      threshold, otherwise the marketplace-wide summary (clearly labelled).
//   2. Trend source — the local band's own quarterly trend WHEN the feed
//      certifies one for this scope (returns it non-null), otherwise the
//      marketplace-wide trend. This differs from boats, which always render the
//      marketplace trend; pet volume clears the per-scope floor far more often,
//      and the feed nulls scopes too thin to narrate. See
//      docs/adr/0003-pet-cost-snapshot-under-ssr.md.
//   3. Mileage source — same rule as the trend (MAR-82): the selected band's
//      own mileage when usable, otherwise the marketplace's. Resolved here
//      rather than in the at-a-glance block so any later cost-section use of
//      trip distance reads the same selection instead of re-deriving it.
// The component renders the returned strings/data; all copy formatting (date
// window, calculator intro, headings) stays in the component.

type CostSectionViewArgs = {
  // Present on city pages ("Los Angeles, CA"); omitted on state pages.
  cityLabel?: string
  stateLabel: string
  costBand: CostSummaryBand | null
  marketplaceCost: MarketplaceCostSummary | null
}

export type CostSectionView = {
  // The location the LOCAL band/trend describes ("Los Angeles, CA" on a city
  // page, "California" once a city page has fallen back to the state entry), or
  // null when the page has no local scope at all. Exposed because the band and
  // trend label themselves in full sentences, and callers that need the bare
  // location (the at-a-glance block's per-stat scope line) must not re-derive it
  // — a city page whose band fell back to the state has to say "California".
  scopeLabel: string | null
  band: {
    percentiles: CostSummaryPercentiles
    scopeSentence: string
    isLocal: boolean
    comparison: string | null
  } | null
  // Typical trip distance for the scope named by scopeLabel when isLocal, and
  // marketplace-wide otherwise. Null when neither scope carries a usable one;
  // callers omit the figure rather than substituting anything.
  mileage: {
    percentiles: CostSummaryMileage
    isLocal: boolean
  } | null
  trend: {
    data: CostSummaryTrend
    isLocal: boolean
    heading: string
  } | null
}

export function buildCostSectionView({
  cityLabel,
  stateLabel,
  costBand,
  marketplaceCost,
}: CostSectionViewArgs): CostSectionView {
  // A band always labels itself by its OWN scope: a city-page band that fell
  // back to the state entry must say "routes touching {state}", not the city.
  // A city-scope band on a page with no cityLabel (the state page) still falls
  // back to the state, which is why this is not a bare cityLabel read.
  const scopeLabelForBand = (band: CostSummaryBand): string =>
    band.scope === "city" ? cityLabel ?? stateLabel : stateLabel
  const bandScopeLabel = costBand ? scopeLabelForBand(costBand) : null

  let band: CostSectionView["band"] = null

  if (
    costBand &&
    bandScopeLabel &&
    hasFinitePercentiles(costBand.costPerMile)
  ) {
    band = {
      percentiles: costBand.costPerMile,
      scopeSentence: `Based on CitizenShipper marketplace routes touching ${bandScopeLabel}`,
      isLocal: true,
      comparison: marketplaceCost
        ? describeMarketplaceComparison(
            costBand.costPerMile.typical,
            marketplaceCost.band.typical,
          )
        : null,
    }
  } else if (marketplaceCost && hasFinitePercentiles(marketplaceCost.band)) {
    band = {
      percentiles: marketplaceCost.band,
      scopeSentence:
        "Based on pet transports across the entire CitizenShipper marketplace",
      isLocal: false,
      comparison: null,
    }
  }

  // Only consider a trend that has enough quarters to narrate AND chart; a
  // thin/malformed scope (e.g. quarters: []) is treated as no trend.
  const localTrend = hasRenderableTrend(costBand?.trend)
    ? costBand!.trend
    : null
  const marketplaceTrend = hasRenderableTrend(marketplaceCost?.trend)
    ? marketplaceCost!.trend
    : null
  const trendData = localTrend ?? marketplaceTrend
  let trend: CostSectionView["trend"] = null

  if (trendData) {
    const isLocal = Boolean(localTrend)

    trend = {
      data: trendData,
      isLocal,
      heading: isLocal
        ? `Recent cost-per-mile trend for routes touching ${bandScopeLabel}`
        : "Recent marketplace cost-per-mile trend",
    }
  }

  // Distance reads whichever band findCostBand already picked on cost, then
  // falls back to the marketplace, independently of the cost band — a scope
  // can clear the cost floor without carrying a usable distance. There is no
  // separate city -> state step for distance: a city band that won on cost but
  // lacks mileage goes straight to the marketplace figure. isLocal records
  // which supplied it so the copy never attributes a marketplace figure to the
  // page's city.
  const bandMileage = costBand?.mileage
  const fallbackMileage = marketplaceCost?.mileage
  const localMileage = hasUsableMileage(bandMileage) ? bandMileage : null
  const marketplaceMileage = hasUsableMileage(fallbackMileage)
    ? fallbackMileage
    : null
  const mileageData = localMileage ?? marketplaceMileage
  const mileage: CostSectionView["mileage"] = mileageData
    ? { percentiles: mileageData, isLocal: Boolean(localMileage) }
    : null

  return { scopeLabel: bandScopeLabel, band, mileage, trend }
}
